/** Approval Management — tab keys and placeholder rows. */

export const APPROVAL_TABS = {
  SUBSIDIARY: "subsidiaryDivision",
  COST_CENTER: "costCenter",
  APPROVERS: "approvers",
};

/**
 * Designation groups shown as sub-tabs under the Approvers tab.
 *
 * `key` is ours — it is what the URL carries (`?group=`) and must stay
 * URL-safe. `authorityGroup` is what the API switches on; the two are not the
 * same, and sending `key` was wrong for two of the four groups: the backend
 * knows them as MDGCEO / GCFOGCOO / HEADOFSUBSIDIARY / AIRPORTMANAGER, so
 * `gcfo` and `apc` matched nothing and fell through to "Invalid authority
 * group".
 *
 * `mandatory` designations are always part of the approval chain;
 * `optional` ones join it only when an approver is actually configured for
 * them — which is how one cost centre routes to a CFO and another to a CAO
 * under the same authority.
 */
export interface ApproverGroup {
  key: string;
  authorityGroup: string;
  label: string;
  mandatory: string[];
  optional: string[];
  /** Everything selectable on the Create Approver screen. */
  designations: string[];
}

const group = (
  key: string,
  authorityGroup: string,
  label: string,
  mandatory: string[],
  optional: string[] = []
): ApproverGroup => ({
  key,
  authorityGroup,
  label,
  mandatory,
  optional,
  designations: [...mandatory, ...optional],
});

export const APPROVER_GROUPS: ApproverGroup[] = [
  group("mdGceo", "MDGCEO", "MD/GCEO", ["R1", "R2", "R3"], ["GCEO", "MD"]),
  group(
    "gcfo",
    "GCFOGCOO",
    "GCFO/CFO/CAO/COO",
    /* No GCOO: the group's own label lists GCFO/CFO/CAO/COO, and the API's
       name for it (GCFOGCOO) is not a designation anyone holds. */
    ["R1", "R2"],
    ["GCFO", "COO", "CAO", "CFO"]
  ),
  group("headOfSubsidiary", "HEADOFSUBSIDIARY", "Head of Subsidiary (MA Sepang/MASB/UTB/MAN)", [
    "R1",
    "R2",
    "Head of Subsidiary",
  ]),
  /* Chain order: the Secretariat, then the Members, then the Airport Manager. */
  group("apc", "AIRPORTMANAGER", "Airport Procurement Committee(APC)", [
    "APC Secretariat",
    "APC Member",
    "Airport Manager",
  ]),
];

/** The group an approval authority belongs to, by either name. */
export const findApproverGroup = (value: any): ApproverGroup | undefined => {
  const wanted = String(value ?? "").trim().toUpperCase();
  if (!wanted) return undefined;
  return APPROVER_GROUPS.find(
    (entry) =>
      entry.key.toUpperCase() === wanted ||
      entry.authorityGroup.toUpperCase() === wanted ||
      // The API normalises by dropping everything that is not a letter.
      entry.authorityGroup.toUpperCase() === wanted.replace(/[^A-Z]/g, "")
  );
};

export interface ApproverRow {
  key: string;
  designation: string;
  userName: string;
  userEmail: string;
  subsidiary: string;
  costCentre: string;
  lastModifiedDate: string;
  lastModifiedBy: string;
}

export interface AdminEntry {
  key: string;
  name: string;
  email: string;
}

/** Placeholder rows so each tab is reviewable before the API exists. */
export const subsidiaryRows = [
  { key: "1", subsidiary: "RPA Project", costCentreCount: 1, adminCount: 1, createdDate: "12 Mar 2023 12:43 PM", lastModifiedDate: "John", lastUpdatedBy: "johndoe@email.com" },
  { key: "2", subsidiary: "RPA Project", costCentreCount: 3, adminCount: 1, createdDate: "12 Mar 2023 12:43 PM", lastModifiedDate: "John", lastUpdatedBy: "johndoe@email.com" },
  { key: "3", subsidiary: "RPA Project", costCentreCount: 2, adminCount: 1, createdDate: "12 Mar 2023 12:43 PM", lastModifiedDate: "John", lastUpdatedBy: "johndoe@email.com" },
  { key: "4", subsidiary: "RPA Project", costCentreCount: 6, adminCount: 1, createdDate: "12 Mar 2023 12:43 PM", lastModifiedDate: "John", lastUpdatedBy: "johndoe@email.com" },
  { key: "5", subsidiary: "RPA Project", costCentreCount: 4, adminCount: 2, createdDate: "12 Mar 2023 12:43 PM", lastModifiedDate: "John", lastUpdatedBy: "johndoe@email.com" },
  { key: "6", subsidiary: "RPA Project", costCentreCount: 2, adminCount: 1, createdDate: "12 Mar 2023 12:43 PM", lastModifiedDate: "John", lastUpdatedBy: "johndoe@email.com" },
];

export const costCentreRows = subsidiaryRows.map((row, index) => ({
  ...row,
  key: `cc-${index}`,
  costCentre: "Lorem Ipsum",
  adminCount: index === 4 ? 2 : 1,
}));

const approverRow = (key: string, designation: string, subsidiary: string): ApproverRow => ({
  key,
  designation,
  userName: "John Doe",
  userEmail: "Johndoe187@gmail.com",
  subsidiary,
  costCentre: subsidiary,
  lastModifiedDate: "12 Mar 2023 12:43 PM",
  lastModifiedBy: "sarwat.jahan@naditek-myairports.my",
});

/** Placeholder approver rows, keyed by designation group. */
export const approverRowsByGroup: Record<string, ApproverRow[]> = {
  mdGceo: [
    approverRow("1", "R2", "MASB - Engineering"),
    approverRow("2", "R3", "MASB - Operations"),
    approverRow("3", "GCEO", "MASB - Operations"),
    approverRow("4", "MD", "MASB - Operations"),
  ],
  gcfo: [
    approverRow("1", "R1", "MA Sepang"),
    approverRow("2", "R2", "MA Sepang"),
    approverRow("3", "R3", "MA Sepang"),
    approverRow("4", "GCFO", "MA Sepang"),
  ],
  headOfSubsidiary: [
    approverRow("1", "R2", "MASB - Engineering"),
    approverRow("2", "R3", "MASB - Operations"),
    approverRow("3", "GCEO", "MASB - Operations"),
    approverRow("4", "MD", "MASB - Operations"),
  ],
  apc: [
    approverRow("1", "R2", "MASB - Engineering"),
    approverRow("2", "R3", "MASB - Operations"),
    approverRow("3", "GCEO", "MASB - Operations"),
    approverRow("4", "MD", "MASB - Operations"),
  ],
};

/**
 * Sample admin directory.
 *
 * The forms read the real directory from `GET /user/get-admin-user-list`;
 * this list is retained only as reference data for the shapes those selects
 * expect and is not rendered.
 */
export const adminDirectory = [
  { name: "John", email: "johndoe@email.com" },
  { name: "Tanya Hill", email: "tanya.hill@example.com" },
  { name: "Cody Fisher", email: "cody.fisher@example.com" },
  { name: "Devon Lane", email: "devon.lane@example.com" },
];
